import { useRef, useState } from 'react'
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import adminStore from './adminStore'
import { requestImpersonationAndNavigate } from './impersonationFlow'
import { createImpersonationTarget } from './impersonationTarget'
import { membershipRoleLabel, membershipStatusLabel } from '../tenancy/organizationPresentation'

const errorCode = error => error?.response?.data?.error?.code || error?.code || error?.message || 'ADMIN_ERROR'
const askReason = action => window.prompt(`Причина: ${action}`, '')?.trim() || null

export default function PlatformAdminDiagnostics() {
	const navigate = useNavigate()
	const [userId, setUserId] = useState('')
	const [userDiagnostic, setUserDiagnostic] = useState(null)
	const [organizationDiagnostic, setOrganizationDiagnostic] = useState(null)
	const [target, setTarget] = useState(null)
	const [error, setError] = useState(null)
	const [loading, setLoading] = useState(false)
	const epoch = useRef(0)

	const openUser = async () => {
		const id = String(userId).trim()
		if (!/^[1-9]\d*$/.test(id)) { setError('ADMIN_INVALID_TARGET'); return }
		const current = ++epoch.current
		setLoading(true); setError(null); setUserDiagnostic(null); setOrganizationDiagnostic(null); setTarget(null)
		try {
			const value = await adminStore.client.get(`/api/admin/users/${id}`)
			if (epoch.current === current) setUserDiagnostic(value)
		} catch (requestError) {
			if (epoch.current === current) setError(errorCode(requestError))
		} finally { if (epoch.current === current) setLoading(false) }
	}

	const selectMembership = async membership => {
		const nextTarget = createImpersonationTarget({ user: userDiagnostic?.user, membership })
		if (!nextTarget) { setError('ADMIN_INVALID_TARGET'); return }
		const current = ++epoch.current
		setTarget(nextTarget); setOrganizationDiagnostic(null); setError(null); setLoading(true)
		try {
			const value = await adminStore.client.get(`/api/admin/organizations/${nextTarget.organizationId}`)
			if (epoch.current === current) setOrganizationDiagnostic(value)
		} catch (requestError) {
			if (epoch.current === current) { setTarget(null); setError(errorCode(requestError)) }
		} finally { if (epoch.current === current) setLoading(false) }
	}

	const impersonate = async () => {
		if (!target) { setError('ADMIN_EXPLICIT_ORGANIZATION_REQUIRED'); return }
		const reason = askReason('диагностический просмотр организации')
		if (!reason) return
		try { await requestImpersonationAndNavigate({ store: adminStore, navigate, target, reason }) }
		catch (requestError) { setError(errorCode(requestError)) }
	}

	return <Stack spacing={2}>
		<Alert severity="info">Диагностика администратора платформы требует явного выбора пользователя, активного участия и организации. Обычная выбранная организация CRM здесь не используется.</Alert>
		<Stack direction="row" spacing={1}><TextField label="ID пользователя" value={userId} onChange={event => setUserId(event.target.value)} /><Button variant="contained" onClick={openUser}>Открыть пользователя</Button></Stack>
		{loading && <CircularProgress />}{error && <Alert severity="error">{error}</Alert>}
		{userDiagnostic && <Card><CardContent><Typography variant="h6">{userDiagnostic.user.email}</Typography><Typography variant="body2">Выберите точное активное участие; выбор не делается автоматически.</Typography><Stack spacing={1} mt={2}>{userDiagnostic.memberships?.map(membership => <Button key={membership.id} variant={target?.membershipId === Number(membership.id) ? 'contained' : 'outlined'} disabled={membership.status !== 'ACTIVE'} onClick={() => selectMembership(membership)}>{membership.organization_name || `Организация #${membership.organization_id}`} · {membershipRoleLabel(membership.role)} · {membershipStatusLabel(membership.status)}</Button>)}</Stack></CardContent></Card>}
		{target && organizationDiagnostic && <Card><CardContent><Typography variant="h6">Организация: {organizationDiagnostic.organization?.name || `#${target.organizationId}`}</Typography><Typography variant="body2">Цель: пользователь #{target.effectiveUserId} · участие #{target.membershipId} · организация #{target.organizationId}</Typography><Button sx={{ mt: 2 }} onClick={impersonate}>Открыть диагностический просмотр только для чтения</Button><Box component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{JSON.stringify(organizationDiagnostic.organization, null, 2)}</Box></CardContent></Card>}
	</Stack>
}
